import { Check } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/shared/lib/utils'
import {
  getMachineryDirectiveOnboardingSidebarStepId,
  isSidebarStepCompleteForProgress,
  isSidebarStepNavigable,
  type MachineryDirectiveOnboardingSidebarStepId,
} from './machineryDirectiveOnboardingSteps'
import type { OnboardingStep } from './machineryDirectiveOnboardingTypes'

type StepMeta = {
  stepId: MachineryDirectiveOnboardingSidebarStepId
  displayStep: number
  titleKey: string
}

const STEP_META: StepMeta[] = [
  {
    stepId: 'intro',
    displayStep: 1,
    titleKey: 'onboarding.stepOverview.intro.title',
  },
  {
    stepId: 'e_plan',
    displayStep: 2,
    titleKey: 'onboarding.stepOverview.ePlan.title',
  },
  {
    stepId: 'high_risk',
    displayStep: 3,
    titleKey: 'onboarding.stepOverview.highRisk.title',
  },
  {
    stepId: 'machine_completion',
    displayStep: 4,
    titleKey: 'onboarding.stepOverview.machineCompletion.title',
  },
  {
    stepId: 'machine_locations',
    displayStep: 5,
    titleKey: 'onboarding.stepOverview.machineLocations.title',
  },
  {
    stepId: 'standards',
    displayStep: 6,
    titleKey: 'onboarding.stepOverview.standards.title',
  },
  {
    stepId: 'type_b_questionnaire',
    displayStep: 7,
    titleKey: 'onboarding.stepOverview.typeB.title',
  },
]

export function MachineryDirectiveOnboardingStepsOverview({
  currentStep,
  furthestProgressSidebarStepId,
  isStepNavigationDisabled = false,
  onNavigateToSidebarStep,
}: {
  currentStep: OnboardingStep
  furthestProgressSidebarStepId: MachineryDirectiveOnboardingSidebarStepId
  isStepNavigationDisabled?: boolean
  onNavigateToSidebarStep?: (
    stepId: MachineryDirectiveOnboardingSidebarStepId
  ) => void
}) {
  const { t } = useTranslation('machineryDirectiveCompliance')
  const currentStepId =
    getMachineryDirectiveOnboardingSidebarStepId(currentStep)

  return (
    <ol className='space-y-0' aria-label={t('onboarding.overviewAriaLabel')}>
      {STEP_META.map((item, index) => {
        const isComplete = isSidebarStepCompleteForProgress(
          item.stepId,
          currentStepId,
          furthestProgressSidebarStepId
        )
        const isCurrent = currentStepId === item.stepId
        const isLast = index === STEP_META.length - 1
        const isNavigable =
          isSidebarStepNavigable(item.stepId, furthestProgressSidebarStepId) &&
          !isStepNavigationDisabled
        const isClickable =
          isNavigable && !isCurrent && onNavigateToSidebarStep != null
        const stepTitle = t(item.titleKey)
        const stepContent = (
          <>
            <div className='flex flex-col items-center'>
              <span
                className={cn(
                  'flex size-6 shrink-0 items-center justify-center rounded-full border-2 text-sm font-semibold',
                  isComplete &&
                    'border-primary bg-primary text-primary-foreground',
                  isCurrent &&
                    !isComplete &&
                    'border-primary bg-background text-primary',
                  !isCurrent &&
                    !isComplete &&
                    'border-muted-foreground/35 text-muted-foreground'
                )}
              >
                {isComplete ? (
                  <Check className='size-4' strokeWidth={2.5} aria-hidden />
                ) : (
                  item.displayStep
                )}
              </span>
              {!isLast && (
                <span
                  className={cn(
                    'my-2 min-h-4 w-px shrink-0',
                    isComplete ? 'bg-primary' : 'bg-border'
                  )}
                  aria-hidden
                />
              )}
            </div>
            <div className='pt-1 pb-4'>
              <p
                className={cn(
                  'text-sm leading-snug font-medium',
                  isCurrent && 'text-foreground',
                  !isCurrent && isNavigable && 'text-foreground',
                  !isCurrent && !isNavigable && 'text-muted-foreground'
                )}
              >
                {stepTitle}
              </p>
            </div>
          </>
        )

        return (
          <li
            key={item.stepId}
            aria-current={isCurrent ? 'step' : undefined}
          >
            {isClickable ? (
              <button
                type='button'
                className='flex w-full cursor-pointer gap-4 text-start focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
                aria-label={t('onboarding.stepOverview.goToStepAriaLabel', {
                  stepNumber: item.displayStep,
                  stepTitle,
                })}
                onClick={() => onNavigateToSidebarStep(item.stepId)}
              >
                {stepContent}
              </button>
            ) : (
              <div className='flex gap-4'>{stepContent}</div>
            )}
          </li>
        )
      })}
    </ol>
  )
}

export function getMachineryDirectiveOnboardingStepHeader(
  currentStep: OnboardingStep,
  translate: (key: string) => string
): string {
  const currentStepId =
    getMachineryDirectiveOnboardingSidebarStepId(currentStep)
  const meta = STEP_META.find((item) => item.stepId === currentStepId)

  if (!meta) {
    return translate('onboarding.overviewTitle')
  }

  return translate(meta.titleKey)
}

export function getMachineryDirectiveOnboardingSidebarStepCount(): number {
  return STEP_META.length
}

export function getMachineryDirectiveOnboardingSidebarDisplayStep(
  currentStep: OnboardingStep
): number {
  const currentStepId =
    getMachineryDirectiveOnboardingSidebarStepId(currentStep)
  const metaIndex = STEP_META.findIndex((item) => item.stepId === currentStepId)
  if (metaIndex < 0) {
    return 1
  }
  return STEP_META[metaIndex]?.displayStep ?? 1
}
